/*
 * Salesforce Voice (Partner Telephony) connector for BroadWorks, via sf_bw_bridge.
 *
 * Runs in the hidden Salesforce Voice iframe (the Contact Center "Adapter URL").
 * - Salesforce calls the methods below when the agent clicks Accept, Hold, Transfer, ...
 *   Each one becomes a REST call to the bridge, which sends it to BroadWorks XSI-Actions.
 * - The bridge streams BroadWorks call events back (Server-Sent Events). They are
 *   turned into Salesforce events (CALL_STARTED, CALL_CONNECTED, HOLD_TOGGLE, HANGUP, ...).
 *
 * Audio never touches the browser: the agent's BroadWorks phone is reported as a DESK_PHONE.
 * Written against @salesforce/scv-connector-base 4.x.
 */
import {
    Constants, VendorConnector, TelephonyConnector, publishEvent, log,
    ActiveCallsResult, AgentConfigResult, VoiceCapabilitiesResult, SharedCapabilitiesResult,
    ParticipantResult, CallResult, HangupResult, HoldToggleResult, InitResult, GenericResult,
    MuteToggleResult, LogoutResult, RecordingToggleResult, PhoneContactsResult, SignedRecordingUrlResult,
    CallInfo, PhoneCall, PhoneCallAttributes, Contact, Phone
} from '@salesforce/scv-connector-base';
import { BridgeClient } from './bridge-client';
import { showLoginForm, hideLoginForm, showNotice } from './login';
import { rlog } from './remote-log';
import { LOGIN_CHANNEL, LOGIN_OK } from './login-channel';
import { bridgeUrl, findBridge, salesforceOrg, NotSetUpError, rememberProblem } from './bridge-url';

// Omni-Channel presence status API name -> BroadWorks Call Center ACD state.
// Unlisted statuses fall back on the generic agentStatus (Online / Offline / AfterCallWork).
const PRESENCE_TO_ACD = {
    Available_for_Voice: 'Available',
    Busy: 'Unavailable',
    Online: 'Available',
    Offline: 'Unavailable',
    AfterCallWork: 'Wrap-Up'
};
const USE_ACD = true;          // false if agents are not BroadWorks Call Center agents

const { CALL_STATE, CALL_TYPE, PARTICIPANT_TYPE, VOICE_EVENT_TYPE, SHARED_EVENT_TYPE, PHONE_TYPE } = Constants;

class BroadWorksTelephony extends TelephonyConnector {
    constructor(owner) {
        super();
        this.o = owner;
    }

    get bridge() { return this.o.bridge; }

    async getActiveCalls() {
        return new ActiveCallsResult({ activeCalls: Object.values(this.o.calls) });
    }

    async acceptCall(call) {
        const c = this.o.find(call);
        await this.bridge.callAction(c.callId, 'answer');
        c.state = CALL_STATE.CONNECTED;
        c.callAttributes.state = CALL_STATE.CONNECTED;
        this.o.acceptedBySalesforce.add(c.callId);
        return new CallResult({ call: c });
    }

    async declineCall(call) {
        const c = this.o.find(call || { callAttributes: { participantType: PARTICIPANT_TYPE.INITIAL_CALLER } });
        await this.bridge.callAction(c.callId, 'decline');
        return new CallResult({ call: c });
    }

    async endCall(call) {
        const c = this.o.find(call);
        const caller = this.o.byParticipant(PARTICIPANT_TYPE.INITIAL_CALLER);
        const third = this.o.byParticipant(PARTICIPANT_TYPE.THIRD_PARTY);
        if (c.callAttributes.participantType === PARTICIPANT_TYPE.AGENT && caller && third) {
            // Agent leaves a consult: BroadWorks connects customer and third party (attended transfer).
            await this.bridge.callAction(caller.callId, 'complete_transfer', { targetCallId: third.callId });
            return new HangupResult({ calls: [caller, third] });
        }
        const target = c.callAttributes.participantType === PARTICIPANT_TYPE.AGENT ? (caller || third) : c;
        await this.bridge.callAction(target.callId, 'end');
        return new HangupResult({ calls: [target] });
    }

    async mute() { return new MuteToggleResult({ isMuted: false }); }      // hasMute: false
    async unmute() { return new MuteToggleResult({ isMuted: false }); }

    async hold(call) {
        const c = this.o.find(call);
        await this.bridge.callAction(c.callId, 'hold');
        return this.o.holdResult(c.callId, true);
    }

    async resume(call) {
        const c = this.o.find(call);
        await this.bridge.callAction(c.callId, 'resume');
        return this.o.holdResult(c.callId, false);
    }

    async dial(contact) {
        const res = await this.bridge.post('/api/dial', { number: contact.phoneNumber });
        const call = this.o.makeCall({
            callId: res.callId, callType: CALL_TYPE.OUTBOUND, phoneNumber: res.phoneNumber, contact,
            state: CALL_STATE.RINGING, participantType: PARTICIPANT_TYPE.INITIAL_CALLER
        });
        return new CallResult({ call });
    }

    async sendDigits(digits) {
        const c = this.o.activeCall();
        if (c) await this.bridge.callAction(c.callId, 'dtmf', { digits });
    }

    async getPhoneContacts() { return new PhoneContactsResult({ contacts: [] }); }

    async swap(call1, call2) {
        const [a, b] = [this.o.find(call1), this.o.find(call2)];
        const [held, active] = a.callInfo.isOnHold ? [a, b] : [b, a];
        await this.bridge.callAction(active.callId, 'hold');
        await this.bridge.callAction(held.callId, 'resume');
        return this.o.holdResult();
    }

    async conference(calls) {
        await this.bridge.post('/api/conference', { callIds: calls.map(c => this.o.find(c).callId) });
        Object.values(this.o.calls).forEach(c => { c.callInfo.isOnHold = false; c.callAttributes.isOnHold = false; });
        return new HoldToggleResult({ isThirdPartyOnHold: false, isCustomerOnHold: false, calls: this.o.calls, isCallMerged: true });
    }

    async addParticipant(contact, parentCall, isBlindTransfer) {
        const parent = this.o.find(parentCall);
        const number = contact.phoneNumber;
        if (isBlindTransfer) {
            await this.bridge.callAction(parent.callId, 'blind_transfer', { number });
            return new ParticipantResult({ initialCallHasEnded: true, callInfo: new CallInfo({ isOnHold: false }),
                                           phoneNumber: number, callId: parent.callId });
        }
        const res = await this.bridge.callAction(parent.callId, 'consult', { number });
        this.o.markHold(parent.callId, true);
        this.o.makeCall({
            callId: res.callId, callType: CALL_TYPE.ADD_PARTICIPANT, phoneNumber: number, contact,
            state: CALL_STATE.RINGING, participantType: PARTICIPANT_TYPE.THIRD_PARTY,
            voiceCallId: parent.callAttributes.voiceCallId
        });
        return new ParticipantResult({ initialCallHasEnded: false, callInfo: new CallInfo({ isOnHold: false }),
                                       phoneNumber: number, callId: res.callId });
    }

    async pauseRecording() { return new RecordingToggleResult({ isRecordingPaused: false }); }
    async resumeRecording() { return new RecordingToggleResult({ isRecordingPaused: false }); }

    // Salesforce's recording player: the VoiceCall's recording location -> a short-lived playable link.
    async getSignedRecordingUrl(recordingUrl, vendorCallKey, callId) {
        const res = await this.bridge.post('/api/recordings/sign', { recordingUrl, vendorCallKey });
        rlog(`recording link issued for ${vendorCallKey}`);
        return new SignedRecordingUrlResult({ success: true, url: res.url, callId });
    }

    async getAgentConfig() {
        return new AgentConfigResult({ phones: [PHONE_TYPE.DESK_PHONE],
                                       selectedPhone: new Phone({ type: PHONE_TYPE.DESK_PHONE }) });
    }

    async setAgentConfig() { return new GenericResult({ success: true }); }

    async getVoiceCapabilities() {
        return new VoiceCapabilitiesResult({
            hasMute: false, hasRecord: false, hasMerge: true, hasSwap: true, hasSignedRecordingUrl: true,
            hasBlindTransfer: true, canConsult: true, hasPhoneBook: false
        });
    }

    async wrapUpCall() {
        if (USE_ACD) await this.bridge.put('/api/agent/state', { state: 'Available' }).catch(e => log(e.message));
    }
}

export class BroadWorksConnector extends VendorConnector {
    constructor() {
        super();
        this.bridge = new BridgeClient(bridgeUrl());
        this.telephony = new BroadWorksTelephony(this);
        this.calls = {};                    // callId -> PhoneCall
        this.acceptedBySalesforce = new Set();
    }

    // ------------------------------------------------------------------ setup
    async init(callCenterConfig) {
        this.config = callCenterConfig;
        const cfg = callCenterConfig || {};
        rlog(`init called; Salesforce org ${cfg.organizationId}, contact center ${cfg['/reqGeneralInfo/reqInternalName']}, ` +
             `SCRT ${cfg.scrtUrl}, vendor ${cfg['/reqGeneralInfo/reqVendorInfoApiName']}`);
        try {
            try {
                this.bridge.setBase(await findBridge(cfg.organizationId));
                this.bridge.orgId = salesforceOrg();
                rlog(`init: bridge ${this.bridge.base}`);
            } catch (e) {
                // Show the reason where the agent looks: the sign-in frame (this one, or the login page).
                console.error('[BroadWorks connector]', e.message);
                this.problem = e.message;
                rememberProblem(e.message);
                this.listenForLoginPage();
                showLoginForm(async () => { throw new Error(e.message); });
                showNotice(e.message);
                if (e instanceof NotSetUpError) return new InitResult({ showLogin: true, loginFrameHeight: 320 });
                throw e;
            }
            if (await this.bridge.resume()) {
                rlog('init: existing bridge session found, starting event stream');
                await this.startStream();
                return new InitResult({});
            }
            // Salesforce shows either this frame or the provider's login page (login.html).
            this.listenForLoginPage();
            showLoginForm(async (userId, password) => {
                rlog(`login attempt for ${userId}`);
                try {
                    await this.bridge.login(userId, password);        // throws with a message on failure
                } catch (e) {
                    rlog('login failed:', e);
                    throw e;
                }
                await this.completeLogin();
            });
            rlog('init: no bridge session, asking Salesforce to show the login frame');
            return new InitResult({ showLogin: true, loginFrameHeight: 320 });
        } catch (e) {
            rlog('init failed:', e);
            throw e;
        }
    }

    listenForLoginPage() {
        if (this.loginChannel || typeof BroadcastChannel === 'undefined') return;
        this.loginChannel = new BroadcastChannel(LOGIN_CHANNEL);
        this.loginChannel.onmessage = (e) => {
            if (e.data && e.data.type === 'WHICH_BRIDGE') {
                this.loginChannel.postMessage(this.problem ? { type: 'BRIDGE', error: this.problem }
                    : { type: 'BRIDGE', bridge: this.bridge.base, org: this.bridge.orgId });
                return;
            }
            if (!e.data || e.data.type !== LOGIN_OK || !e.data.token) return;
            rlog('session received from the login page');
            this.bridge.useToken(e.data.token);
            this.completeLogin().catch((err) => rlog('completing login failed:', err));
        };
    }

    async completeLogin() {
        if (this.loggedIn) return;
        this.loggedIn = true;
        hideLoginForm();
        await this.startStream();
        publishEvent({ eventType: SHARED_EVENT_TYPE.LOGIN_RESULT, payload: new GenericResult({ success: true }) });
        rlog('login succeeded, LOGIN_RESULT sent to Salesforce');
    }

    async getTelephonyConnector() { return this.telephony; }

    async getSharedCapabilities() {
        return new SharedCapabilitiesResult({ hasContactSearch: false, hasAgentAvailability: false });
    }

    async setAgentStatus(agentStatus, statusInfo = {}) {
        if (USE_ACD) {
            const state = PRESENCE_TO_ACD[statusInfo.statusApiName] || PRESENCE_TO_ACD[agentStatus];
            if (state) await this.bridge.put('/api/agent/state', { state });
        }
        return new GenericResult({ success: true });
    }

    async logout() {
        await this.bridge.logout();
        this.calls = {};
        this.loggedIn = false;
        return new LogoutResult({ success: true, loginFrameHeight: 320 });
    }

    onAgentWorkEvent() {}
    handleMessage() {}
    downloadLogs() {}

    async startStream() {
        this.bridge.openEvents(msg => this.onBridgeEvent(msg), () => {
            publishEvent({ eventType: SHARED_EVENT_TYPE.LOGOUT_RESULT, payload: new LogoutResult({ success: true }) });
        });
    }

    // ---------------------------------------------------------- call registry
    makeCall({ callId, callType, phoneNumber, contact, state, participantType, voiceCallId, isOnHold = false }) {
        const call = new PhoneCall({
            callId, callType, phoneNumber, state,
            contact: contact || new Contact({ phoneNumber }),
            callInfo: new CallInfo({ isOnHold, isSoftphoneCall: false, callStateTimestamp: new Date() }),
            callAttributes: new PhoneCallAttributes({ voiceCallId: voiceCallId || undefined, participantType, isOnHold })
        });
        this.calls[callId] = call;
        return call;
    }

    find(call) {
        if (call && call.callId && this.calls[call.callId]) return this.calls[call.callId];
        const type = call && call.callAttributes && call.callAttributes.participantType;
        const found = type && type !== PARTICIPANT_TYPE.AGENT ? this.byParticipant(type) : null;
        if (found) return found;
        if (type === PARTICIPANT_TYPE.AGENT) return { callId: null, callAttributes: { participantType: type } };
        throw new Error(`Unknown call ${call && call.callId}`);
    }

    byParticipant(type) {
        return Object.values(this.calls).find(c => c.callAttributes.participantType === type);
    }

    activeCall() {
        return Object.values(this.calls).find(c => !c.callInfo.isOnHold && c.state === CALL_STATE.CONNECTED);
    }

    markHold(callId, onHold) {
        const c = this.calls[callId];
        if (c) { c.callInfo.isOnHold = onHold; c.callAttributes.isOnHold = onHold; }
    }

    holdResult(callId, onHold) {
        if (callId) this.markHold(callId, onHold);
        const caller = this.byParticipant(PARTICIPANT_TYPE.INITIAL_CALLER);
        const third = this.byParticipant(PARTICIPANT_TYPE.THIRD_PARTY);
        return new HoldToggleResult({
            isCustomerOnHold: !!(caller && caller.callInfo.isOnHold),
            isThirdPartyOnHold: !!(third && third.callInfo.isOnHold),
            calls: this.calls
        });
    }

    // ------------------------------------------------------- bridge -> Salesforce
    onBridgeEvent(msg) {
        const b = msg.call || {};
        const existing = b.callId && this.calls[b.callId];
        switch (msg.type) {
        case 'SNAPSHOT':
            // After a reload: rebuild calls BroadWorks still has.
            (msg.calls || []).forEach(c => this.calls[c.callId] || this.makeCall({
                callId: c.callId, phoneNumber: c.phoneNumber, voiceCallId: c.voiceCallId,
                callType: c.callType === 'inbound' ? CALL_TYPE.INBOUND : CALL_TYPE.OUTBOUND,
                state: c.state === 'connected' ? CALL_STATE.CONNECTED : CALL_STATE.RINGING,
                participantType: PARTICIPANT_TYPE.INITIAL_CALLER, isOnHold: c.isOnHold
            }));
            break;
        case 'CALL_STARTED': {
            if (existing) break;
            const call = this.makeCall({
                callId: b.callId, phoneNumber: b.phoneNumber, voiceCallId: b.voiceCallId,
                callType: b.callType === 'inbound' ? CALL_TYPE.INBOUND : CALL_TYPE.OUTBOUND,
                state: CALL_STATE.RINGING, participantType: PARTICIPANT_TYPE.INITIAL_CALLER
            });
            publishEvent({ eventType: VOICE_EVENT_TYPE.CALL_STARTED, payload: new CallResult({ call }) });
            break;
        }
        case 'CALL_CONNECTED': {
            if (!existing) break;
            const wasAccepted = this.acceptedBySalesforce.delete(b.callId);
            existing.state = existing.callAttributes.state = CALL_STATE.CONNECTED;
            if (existing.callAttributes.participantType === PARTICIPANT_TYPE.THIRD_PARTY) {
                publishEvent({ eventType: VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED, payload: new ParticipantResult({
                    initialCallHasEnded: !this.byParticipant(PARTICIPANT_TYPE.INITIAL_CALLER),
                    callInfo: new CallInfo({ isOnHold: false }), phoneNumber: existing.phoneNumber, callId: existing.callId
                }) });
            } else if (!wasAccepted) {
                // Answered on the handset, or the far end picked up an outbound call.
                publishEvent({ eventType: VOICE_EVENT_TYPE.CALL_CONNECTED, payload: new CallResult({ call: existing }) });
            }
            break;
        }
        case 'HOLD_TOGGLE':
            if (!existing) break;
            publishEvent({ eventType: VOICE_EVENT_TYPE.HOLD_TOGGLE, payload: this.holdResult(b.callId, msg.isOnHold) });
            break;
        case 'HANGUP': {
            if (!existing) break;
            delete this.calls[b.callId];
            this.acceptedBySalesforce.delete(b.callId);
            existing.state = CALL_STATE.ENDED;
            existing.reason = msg.reason === 'missed' ? Constants.HANGUP_REASON.PHONE_CALL_ENDED : msg.reason;
            if (existing.callAttributes.participantType === PARTICIPANT_TYPE.THIRD_PARTY) {
                publishEvent({ eventType: VOICE_EVENT_TYPE.PARTICIPANT_REMOVED, payload: new CallResult({ call: existing }) });
            } else {
                publishEvent({ eventType: VOICE_EVENT_TYPE.HANGUP, payload: new HangupResult({ calls: [existing] }) });
            }
            break;
        }
        case 'AGENT_STATE':
            log(`BroadWorks ACD state is now ${msg.state}`);
            break;
        default:
            break;
        }
    }
}
